/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import {
  Button,
  Label,
  Modal,
  type ModalProps,
  Segment,
} from '@mairistem/composants';
import { _fn } from '@mairistem/administres-utils';

import type { Individu } from '@/types';

import PanelModalEditionConfirmClose from './PanelModalEditionConfirmClose';

interface PanelModalEditionProps {
  children: React.ReactNode;
  errors: Partial<Individu> & { api?: boolean };
  loading: boolean;
  header: string;
  onSave: (data: object) => void;
  onClose: () => void;
}

const PanelModalEdition = (props: PanelModalEditionProps & ModalProps) => {
  const {
    children,
    errors,
    loading,
    header,
    onSave,
    onClose,
    updated,
    ...rest
  } = props;

  const [openConfirm, setOpenConfirm] = React.useState<boolean>(false);

  const error: string = React.useMemo(() => {
    if (errors?.api) {
      return "Une erreur est survenue lors de l'enregistrement";
    }
    if (_.size(errors) === 1) {
      return _.values(errors)[0] as string;
    }
    if (_.size(errors) > 1) {
      return 'Une erreur est survenue, veuillez vérifier vos données.';
    }

    return null;
  }, [errors]);

  const handleClose = React.useCallback(() => {
    if (updated) {
      setOpenConfirm(true);
    } else {
      _fn(onClose);
    }
  }, [onClose, updated]);

  return (
    <>
      <Modal closeIcon open onClose={handleClose} {...rest}>
        <Modal.Header>{header}</Modal.Header>
        <Modal.Content scrolling={false}>
          {!_.isNil(error) && (
            <Segment
              basic
              style={{
                marginLeft: '-0.5em',
                padding: 0,
              }}
            >
              <Label color="red" ribbon>
                {error}
              </Label>
            </Segment>
          )}
          {children}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={handleClose}
            disabled={loading}
            basic
            size="small"
            icon="close"
            content="Annuler"
            data-testid="fiche-panel-modal-edition-annuler"
          />
          <Button
            onClick={onSave}
            disabled={loading}
            loading={loading}
            positive
            icon="checkmark"
            content="Enregistrer"
            data-testid="fiche-panel-modal-edition-enregistrer"
          />
        </Modal.Actions>
      </Modal>
      {openConfirm && (
        <PanelModalEditionConfirmClose
          onConfirm={onClose}
          onCancel={() => setOpenConfirm(false)}
        />
      )}
    </>
  );
};

export default PanelModalEdition;
